import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, Link} from 'react-router-dom';
import { Container, Typography, Button, Box, CssBaseline } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import theme from './theme/theme.js';
import ScrollHandler from './components/ScrollHandler.js';
import HeaderSection from './components/HeaderSection.js';
import Footer from './components/Footer.js';
import MobileAppModal from './components/MobileAppModal';
import { UserProvider } from './UserContext';
import { getDecryptedAuthCookie, clearAuthCookie } from './components/authUtils';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Global settings for all queries
      refetchOnWindowFocus: false,
      refetchOnReconnect: true,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes (renamed from cacheTime)
    },
    mutations: {
      // Global settings for all mutations
      retry: 1,
    },
  },
});

// Shared authentication check function
const useAuthenticationCheck = () => {
  const [isCheckingAuth, setIsCheckingAuth] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  
  React.useEffect(() => {
    const checkAuthentication = () => {
      // More robust authentication check
      let authStatus = localStorage.getItem('isAuthenticated') === 'true';
      
      // Check if we have valid user data
      const userId = localStorage.getItem('user_id');
      const userEmail = localStorage.getItem('email');
      
      if (!authStatus || !userId || !userEmail) {
        // Try to get data from cookies as fallback
        const cookieData = getDecryptedAuthCookie();
        if (cookieData && cookieData.isAuthenticated && cookieData.user_id && cookieData.email) {
          Object.entries(cookieData).forEach(([key, value]) => {
            localStorage.setItem(key, value);
          });
          authStatus = true;
        } else {
          // Clear any stale data
          localStorage.clear();
          sessionStorage.clear();
          clearAuthCookie();
          authStatus = false;
        }
      }
      
      setIsAuthenticated(authStatus);
      setIsCheckingAuth(false);
    };
    
    checkAuthentication();
  }, []);
  
  return { isCheckingAuth, isAuthenticated };
};

const ProtectedRoute = ({ children }) => {
  const { isCheckingAuth, isAuthenticated } = useAuthenticationCheck();
  
  if (isCheckingAuth) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Typography variant="h6" color="primary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

const RootRouteHandler = () => {
  const { isCheckingAuth, isAuthenticated } = useAuthenticationCheck();
  
  if (isCheckingAuth) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh' 
      }}>
        <Typography variant="h6" color="primary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }
  
  // If authenticated, redirect to home page
  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }
  
  // If not authenticated, show landing page
  return <LandingPage />;
};


const MainLayout = ({ children }) => {
  const [user, setUser] = React.useState({
    firstName: localStorage.getItem('first_name'),
    lastName: localStorage.getItem('last_name'),
  });
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const handleLogout = async () => {
    try {
      // Call backend logout API
      const userId = localStorage.getItem('user_id');
      if (userId) {
        const { userLogout } = await import('./api/apiClient');
        await userLogout(userId);
      }
    } catch (error) {
      console.error('Logout API error:', error);
      // Continue with client-side cleanup regardless
    }
    
    // Clear all authentication data
    localStorage.clear();
    sessionStorage.clear();
    clearAuthCookie();
    
    // Clear browser history to prevent back button issues
    window.history.replaceState(null, null, '/');
    
    // Force redirect to landing page
    window.location.href = '/';
  };
  const handleMobileMenuOpen = () => setMobileMenuOpen(true);
  React.useEffect(() => {
    const syncUserFromStorage = () => {
      setUser({
        firstName: localStorage.getItem('first_name'),
        lastName: localStorage.getItem('last_name'),
      });
    };
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Re-check authentication when page becomes visible (e.g., back button)
        const isAuth = localStorage.getItem('isAuthenticated') === 'true';
        const userId = localStorage.getItem('user_id');
        const userEmail = localStorage.getItem('email');
        
        if (!isAuth || !userId || !userEmail) {
          // Clear stale data and redirect to login
          localStorage.clear();
          sessionStorage.clear();
          clearAuthCookie();
          window.location.href = '/login';
        }
      }
    };
    
    window.addEventListener('storage', syncUserFromStorage);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      window.removeEventListener('storage', syncUserFromStorage);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  React.useEffect(() => {
    setUser({
      firstName: localStorage.getItem('first_name'),
      lastName: localStorage.getItem('last_name'),
    });
  }, [window.location.pathname]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <HeaderSection
       user={{ firstName: user.firstName, lastName: user.lastName }}
        onLogout={handleLogout}
        onMobileMenuOpen={handleMobileMenuOpen}
      />
      <Box component="main" sx={{ flexGrow: 1 }}>
        {children}
      </Box>
      <Footer />
    </Box>
  );
};

// Replace direct imports of pages with lazy imports
const Home = lazy(() => import('./pages/Home.js'));
const Reports = lazy(() => import('./pages/Reports.js'));
const LoginPage = lazy(() => import('./pages/LoginPage.js'));
const SignupPage = lazy(() => import('./pages/SignUp.js'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy.js'));
const FAQ = lazy(() => import('./pages/FAQ.js'));
const Terms = lazy(() => import('./pages/Terms.js'));
const AddExpense = lazy(() => import('./pages/AddExpense.js'));
const ExpenseDashboard = lazy(() => import('./pages/Expenses.js'));
const FamilyDashboard = lazy(() => import('./pages/FamilyDashboard.js'));
const UserProfile = lazy(() => import('./pages/UserProfile.js'));
const LandingPage = lazy(() => import('./pages/LandingPage.js'));

// Admin Panel Components
const AdminLogin = lazy(() => import('./pages/AdminLogin.js'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard.js'));
const AdminUsers = lazy(() => import('./pages/AdminUsers.js'));
const AdminReceipts = lazy(() => import('./pages/AdminReceipts.js'));
const AdminFamilies = lazy(() => import('./pages/AdminFamilies.js'));
const AdminAdvancedAnalytics = lazy(() => import('./pages/AdminAdvancedAnalytics.js'));
const AdminSystemMonitoring = lazy(() => import('./pages/AdminSystemMonitoring.js'));

const AdminProfile = lazy(() => import('./pages/AdminProfile.js'));
const AdminChangePassword = lazy(() => import('./pages/AdminChangePassword.js'));
const AdminCreateAdmin = lazy(() => import('./pages/AdminCreateAdmin.js'));
const AdminListAdmins = lazy(() => import('./pages/AdminListAdmins.js'));
const AdminCancellations = lazy(() => import('./pages/AdminCancellations.js'));
const AdminProtectedRoute = lazy(() => import('./components/AdminProtectedRoute.js'));

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={theme}>
        <UserProvider>
          <CssBaseline />
          <MobileAppModal />
          <Router>
            <ScrollHandler />
                      <Suspense fallback={
              <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column',
                justifyContent: 'center', 
                alignItems: 'center', 
                minHeight: '60vh',
                gap: 2
              }}>
                <Typography variant="h6" color="primary">
                  Loading...
                </Typography>
              </Box>
            }>
            <Routes>
              {/* --- Public Routes --- */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />

              {/* --- Protected Routes (Use ProtectedRoute and MODIFIED MainLayout) --- */}
              <Route path="/home" element={<ProtectedRoute><MainLayout><Home /></MainLayout></ProtectedRoute>} />
              <Route path="/reports" element={<ProtectedRoute><MainLayout><Reports /></MainLayout></ProtectedRoute>} />
              <Route path="/add-expense" element={<ProtectedRoute><MainLayout><AddExpense /></MainLayout></ProtectedRoute>} />
              <Route path="/expenses" element={<ProtectedRoute><MainLayout><ExpenseDashboard /></MainLayout></ProtectedRoute>} />
              <Route path="/family-dashboard" element={<ProtectedRoute><MainLayout><FamilyDashboard /></MainLayout></ProtectedRoute>} />
              <Route path="/user-profile" element={<ProtectedRoute><MainLayout><UserProfile /></MainLayout></ProtectedRoute>} />

              {/* --- Root Path with Authentication Check --- */}
              <Route path="/" element={<RootRouteHandler />} />

              {/* --- Static Pages (Terms, Privacy, FAQ) --- */}
              <Route path="/terms" element={<Terms />} />
              <Route path="/privacy" element={<PrivacyPolicy />} />
              <Route path="/faq" element={<FAQ />} />

              {/* --- Admin Panel Routes --- */}
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
              <Route path="/admin/users" element={<AdminProtectedRoute><AdminUsers /></AdminProtectedRoute>} />
              <Route path="/admin/receipts" element={<AdminProtectedRoute><AdminReceipts /></AdminProtectedRoute>} />
              <Route path="/admin/families" element={<AdminProtectedRoute><AdminFamilies /></AdminProtectedRoute>} />
              <Route path="/admin/analytics" element={<AdminProtectedRoute><AdminAdvancedAnalytics /></AdminProtectedRoute>} />
    
              <Route path="/admin/system-monitoring" element={<AdminProtectedRoute><AdminSystemMonitoring /></AdminProtectedRoute>} />
              <Route path="/admin/profile" element={<AdminProtectedRoute><AdminProfile /></AdminProtectedRoute>} />
              <Route path="/admin/change-password" element={<AdminProtectedRoute><AdminChangePassword /></AdminProtectedRoute>} />
              <Route path="/admin/create-admin" element={<AdminProtectedRoute><AdminCreateAdmin /></AdminProtectedRoute>} />
              <Route path="/admin/list-admins" element={<AdminProtectedRoute><AdminListAdmins /></AdminProtectedRoute>} />
              <Route path="/admin/cancellations" element={<AdminProtectedRoute><AdminCancellations /></AdminProtectedRoute>} />

              {/* --- 404 Not Found Route --- */}
              <Route path="*" element={
                <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
                  <HeaderSection pageType="public" />
                  <Container sx={{
                    textAlign: 'center',
                    py: 5,
                    flexGrow: 1,
                    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                    minHeight: 'calc(100vh - 128px)'
                  }}>
                    <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>404</Typography>
                    <Typography variant="h5" component="h2" gutterBottom>Page Not Found</Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                      Sorry, the page you are looking for could not be found or has been moved.
                    </Typography>
                    <Button
                      component={Link}
                      to={localStorage.getItem('isAuthenticated') === 'true' ? '/home' : '/login'}
                      variant="contained"
                      size="large"
                    >
                      Go Back
                    </Button>
                  </Container>
                  <Footer />
                </Box>
              } />
            </Routes>
          </Suspense>
        </Router>
      </UserProvider>
      <ReactQueryDevtools initialIsOpen={false} />
    </ThemeProvider>
    </QueryClientProvider>
  );
}
export default App;
